<template>
  <div>
    <v-data-table
      ref="dataTable"
      :loading="isLoading"
      :headers="customReceiptHeaders"
      :items="receipts"
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      :page="$route.query.page ? Number($route.query.page) : 1"
      class="receipt-table cursor-pointer "
      @update:page="($event) => {
        $vuetify.goTo($refs.dataTable)
        addQuery({ page: $event })
      }"
    >
      <template #loading>
        <v-skeleton-loader type="table" />
      </template>

      <template #top>
        <div class="w-full d-flex">
          <v-spacer />
          <v-btn
            :loading="isLoading"
            :disabled="receipts.length <= 0 || isLoading"
            :ripple="false"
            color="secondary"
            small
            class="ma-2"
            @click="download"
          >
            <v-icon left>
              {{ icons.mdiFileExcel }}
            </v-icon>
            ダウンロード
          </v-btn>
        </div>
      </template>

      <template #[`item.meta.realTotal`]="{ item }">
        {{ item.meta.realTotal ? `¥${item.meta.realTotal.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.subtotal`]="{ item }">
        {{ item.attributes.subtotal ? `¥${item.attributes.subtotal.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.serviceFee`]="{ item }">
        {{ item.attributes.serviceFee ? `¥${item.attributes.serviceFee.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.processingFee`]="{ item }">
        {{ item.attributes.processingFee ? `¥${item.attributes.processingFee.toLocaleString()}` : '-' }}
      </template>

      <template #[`item.attributes.discountFee`]="{ item }">
        {{ item.attributes.discountFee ? `¥${item.attributes.discountFee.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.meta.totalCustomerPayAmount`]="{ item }">
        {{ item.meta.totalCustomerPayAmount ? `¥${item.meta.totalCustomerPayAmount.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.change`]="{ item }">
        <span
          :class="Number(item.attributes.change) < 0 ? 'error--text' : ''"
        >
          {{ `¥${item.attributes.change.toLocaleString()}` }}
        </span>
      </template>

      <template #[`item.attributes.createdAt`]="{item}">
        <span class="text-no-wrap">{{ dateTime(item.attributes.createdAt) }}</span>
      </template>

      <template #[`item.attributes.pamentMethodNames`]="{item}">
        <div
          v-if="item.attributes.pamentMethodNames && item.attributes.pamentMethodNames.length"
          class="d-flex"
        >
          <v-chip
            v-for="(methodName, index) in item.attributes.pamentMethodNames"
            :key="index"
            small
            class="ma-1"
          >
            {{ methodName }}
          </v-chip>
        </div>
      </template>

      <template #[`item.meta.hasIssuedReceiptSlipt`]="{item}">
        <v-chip
          v-if="item.meta.hasIssuedReceiptSlipt"
          color="warning"
          class="v-chip-light-bg warning--text"
          small
        >
          発行済
        </v-chip>
        <v-chip
          v-if="item.meta.hasReprintedReceiptSlip"
          color="error"
          class="v-chip-light-bg error--text"
          small
        >
          再発行
        </v-chip>
      </template>

      <template #[`item.id`]="{item}">
        <v-chip
          v-if="item.id"
          small
          :color="item.attributes.revisit.isRevisit ? 'secondary' : ''"
          @click="$emit('open-receipt', item.id)"
        >
          <clip-loader
            v-if="isLoadingReceipt && +receipt.id === +item.id"
            :loading="true"
            :color="'#8A8D93'"
            size="16px"
            class="d-flex align-items pr-2"
          />
          {{ item.attributes.revisit.isRevisit ? '出戻り' : '' }}伝票 No.{{ item.id }}
        </v-chip>
      </template>

      <template #[`footer.page-text`]="props">
        {{ props.pageStart }} - {{ props.pageStop }} / {{ props.itemsLength }}
      </template>

      <template #[`item.revisit.id`]="{item}">
        <v-btn
          v-if="item.id && !item.attributes.revisit.isRevisit"
          color="primary"
          small
          @click="$emit('revisit', item.attributes.tableId)"
        >
          出戻り
        </v-btn>
        <span
          v-else
        >
          親伝票 No.{{ item.attributes.revisit.revisitReceiptId }}
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { ref, inject, watch } from '@vue/composition-api'
import { mdiEyeOffOutline, mdiFileExcel } from '@mdi/js'
import * as XLSX from 'xlsx/xlsx.mjs'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'
import useDateFormat from '@/views/composable/useDateFormat'
import useQueryString from '@/views/composable/useQueryString'
import useCompInit from '@/views/composable/useCompInit'
import useReceiptsDataTable from '@/views/composable/data-table/useReceiptsDataTable'
import ReceiptApi from '@/api/waiter/Receipt'
import { ReceiptHeaders } from '@/utils/constants/table-headers'

export default {
  components: {
    ClipLoader,
  },
  props: {
    date: {
      type: String,
      required: true,
      default: () => new Date().toISOString().substr(0, 10),
    },
    interval: {
      type: Number,
      required: true,
      default: 0,
    },
    endDate: {
      type: String,
      default: '',
    },
    customerTagId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const { route, addQuery } = useQueryString()
    const { isLoading, initWith } = useCompInit()
    const receipts = ref([])
    const receipt = inject('receipt', {})
    const isLoadingReceipt = inject('isLoadingReceipt', false)
    const isReceiptOpen = inject('isReceiptOpen', false)
    const isDownloading = ref(false)

    const { dateTime } = useDateFormat()
    const { buildXlsxData } = useReceiptsDataTable(receipts)

    const customReceiptHeaders = [
      ...ReceiptHeaders,
      {
        text: '出戻り',
        value: 'revisit.id',
        sortable: false,
      },
    ]

    const getReceipts = async ({
      date = null,
      interval = null,
      endDate = null,
      id = null,
      customerTagId = null,
    }) => {
      const res = await ReceiptApi.getReceipts({
        date,
        interval,
        endDate,
        id,
        customerTagId,
      })

      if (res?.status === 200) {
        receipts.value = [...res.data.receipts.data]
      }
    }

    const download = () => {
      isDownloading.value = true

      const receiptExcel = buildXlsxData()

      const wb = XLSX.utils.book_new()
      const data = XLSX.utils.json_to_sheet(receiptExcel)
      XLSX.utils.book_append_sheet(wb, data, '伝票')

      let title = `${props.date}_${['日', '週', '月'][props.interval]}`
      if (props.endDate) title = `${props.date}~${props.endDate}`
      XLSX.writeFile(wb, `伝票_${title}.xlsx`)

      isDownloading.value = false
    }

    const fetchReceipts = () => {
      const queryParams = {
        ...route.value.query,
        date: props.date,
        interval: props.interval,
        ...(props.endDate ? { endDate: props.endDate } : {}),
        ...(props.customerTagId ? { customerTagId: props.customerTagId } : {}),
      }
      getReceipts(queryParams)
    }

    watch(() => [props.date, props.interval, props.endDate, props.customerTagId], () => {
      fetchReceipts()
    })

    initWith([
      fetchReceipts(),
    ])

    return {
      // data
      receipts,
      isLoading,
      customReceiptHeaders,
      receipt,
      isLoadingReceipt,
      isReceiptOpen,

      // methods
      getReceipts,
      dateTime,
      addQuery,
      download,

      icons: {
        mdiEyeOffOutline,
        mdiFileExcel,
      },
    }
  },
}
</script>

<style lang="scss">
.receipt-table {
  white-space: nowrap;
}
</style>
